import { supabase } from "@/lib/supabaseClient";
import { CompanyReview, Subscription, Profile } from "@/types";
import { IReviewRepository, ISubscriptionRepository, IProfileRepository } from "./Interfaces";

export class SupabaseProfileRepository implements IProfileRepository {
  async getProfileById(userId: string): Promise<Profile | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
    return data as Profile;
  }

  async updateProfile(userId: string, data: Partial<Profile>): Promise<Profile> {
    const { data: updated, error } = await supabase
      .from('profiles')
      .update(data)
      .eq('id', userId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return updated as Profile;
  }
}

export class SupabaseSubscriptionRepository implements ISubscriptionRepository {
  async getSubscriptionByUserId(userId: string): Promise<Subscription | null> {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*, plans(*)')
      .eq('profile_id', userId)
      .single();
    
    if (error) return null;
    return data as Subscription;
  }

  async updateSubscription(userId: string, subData: Partial<Subscription>): Promise<void> {
    const { error } = await supabase
      .from('subscriptions')
      .update(subData)
      .eq('profile_id', userId);
    
    if (error) throw new Error(error.message);
  }
}

const isTableMissingError = (msg: string) =>
  msg.includes("does not exist") || msg.includes("schema cache") || msg.includes("relation");

export class SupabaseReviewRepository implements IReviewRepository {
  async createReview(review: Omit<CompanyReview, 'id' | 'created_at' | 'status'>): Promise<CompanyReview> {
    const { data, error } = await supabase
      .from('company_reviews')
      .insert({ ...review, status: 'pending' })
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data as CompanyReview;
  }

  async getApprovedReviewsByCompany(companyId: string): Promise<CompanyReview[]> {
    const { data, error } = await supabase
      .from('company_reviews')
      .select('*, candidate:candidate_id(full_name)')
      .eq('company_id', companyId)
      .eq('status', 'approved')
      .order('created_at', { ascending: false });

    if (error) {
      if (isTableMissingError(error.message)) return [];
      throw new Error(error.message);
    }
    return data as CompanyReview[];
  }

  async getAverageRating(companyId: string): Promise<number> {
    const { data, error } = await supabase
      .from('company_reviews')
      .select('rating')
      .eq('company_id', companyId)
      .eq('status', 'approved');

    if (error || !data || data.length === 0) return 0;
    const sum = data.reduce((acc, curr) => acc + curr.rating, 0);
    return sum / data.length;
  }
}
